/**
 * 全体の工程表 /schedule（メニュー「工程表」。#102、docs/39 §5.2・§5.3.1・§5.3.2、Figma C3・C4・P1）
 *   見るだけ（バーのドラッグはしない）。直すのは案件の工程表：名前・「工程表を開く」で /groups/:id/schedule?row=<工程> へ
 *   PC・タブレット（768px 以上）：
 *     題の段「工程表」としるし3つ（進行中 n現場・今週 延べ n人・重なり n件＝押すと業者ごとにして最初の重なりの行へ）。
 *       「今週 延べ」と下の「その日の人数」は 人の列（#297、案件の工程表の ⋮「表示設定」の「人」）を ON にした人だけ
 *     操作の列：現場別｜業者別 ／ 日｜旬｜月（#291 で週は旬）／ ‹ 今日 › ／ 期間 …… 業者・現場で探す（画面の中で絞る）。選んだ表示はその人のブラウザに覚える
 *     表：画面の残りの高さいっぱい・中でスクロール。日付の見出し・左の列・下の「その日の人数」は動かない。地の休みは会社の休み
 *     現場別（C4）：1行＝工程表のある{project}。▸ で工程と節目（工程名の下の段に担当。10/1。人数の字は #297 でやめた）。業者別（C3）：業者／自社／書いた担当／担当なし（10/1 から担当なしも最初から開く）、重なりは朱
 *   スマホ（768px 未満）：日ごとの一覧（P1）。‹ 日 ›・今日、左右にスワイプで前後の日
 *   重なり・その日の人数・段分けは packages/contract の関数（overlapsOf・crewByDate・lanesOf・workDatesOf）。API は材料を返すだけ
 * #103（docs/39 §6.3.3・§6.3.5）：表の上に「協力会社からの報告」（確かめ待ちがあるときだけ。R1）、題の段に「反映・見送り記録」、
 *   業者ごとの取引先の行の名前の右に「リンク」（期限の 14日前からは「リンク 12/29 まで」、切れたら「リンク 期限切れ」）→ リンクを送る（L1）
 * 10/1 射場さん（期間の外の工程）：期間の後ろの工程・節目も行に出す（API の ahead=1。後ろにしか無い現場も）。
 *   進行中・これからの現場は、期間がいつでも過ぎた工程も含めて全部行に出す（running=1。先の月へ送っても消えない）。
 *   見えている日の外にあるバーは、表の端に「12/12〜 ›」「‹ 〜10/2」（edgeMarksOf）→ 押すとその日まで表を送る。
 *   操作の列の「遅延工程も表示」（覚える）で、期間より前に終わるはずの遅れている工程も（late=1）、遅れている工程に「n日遅れ」。
 *   スマホは日ごとの一覧の下に「この先の工程（7日）」（upcomingOf）。出力（紙・Excel・CSV）は今のまま期間の工程だけ
 */
import {
  SCHEDULE_JUN_TITLE,
  TASK_STATE_LABEL,
  barDonePct,
  taskStateOf,
  addDays,
  linkChipText,
  assigneeKeyOf,
  calendarForRow,
  crewByDate,
  dateRuns,
  dayNo,
  edgeMarksOf,
  isOffDay,
  lanesOf,
  mdOf,
  mdwLongOf,
  mdwOf,
  overlapCandidate,
  overlapsOf,
  overviewAssigneeName,
  overviewPeopleOf,
  overviewRowsOf,
  overviewSitesOf,
  scheduleHref,
  upcomingOf,
  weekdayOf,
  WEEKDAY_JA,
  ymdOfDayNo,
  type OverlapResult,
  type OverviewPerson,
  type OverviewPersonBar,
  type OverviewRowInfo,
  type ScheduleOverview,
  type ScheduleOverviewItem,
  type ScheduleOverviewRow,
  type WorkCalendar,
} from "@genbaryoku/contract";
import { SCHEDULE_EXPORT_MSG, SCHEDULE_MSG, PARTNER_LINK_MSG } from "./msg.js";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Fragment, useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { CrIcon } from "../../components/Create.jsx";
import { ErrorText, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { useCrewColumn, usePinchZoom, useWide } from "./hooks.js";
import { Pop } from "./Overlap.jsx";
import { PartnerLinkDialog } from "./PartnerLink.jsx";
import { CreateScheduleDialog } from "./Standalone.jsx";
import { ExportDialog } from "./Export.jsx";
import { ReportHistoryButton, ReportsPanel, usePendingReports } from "./Reports.jsx";
import { useTextSize } from "../../components/TextSize.jsx";
import { PX, STEP_DAYS, daysOf, joinedRun, mondayOf, monthStartOf, nameColumnWidth, overviewRangeOf, scaleOf, textWidth, todayLocal, viewOf, workRuns, type View } from "./timeline.js";
import { JunTicks, junTicks, ScaleLines } from "./scale.jsx";
import { type Axis } from "./Gantt.jsx";

/** バーの両端の日付（#291 §2.1.1。旬・月の表示だけ・10px・薄墨）。右は次のバー（とその左の日付）にぶつかれば出さない。1日だけのバーは左の1つだけ（左が期間の外なら右） */
const BDATE_GAP = 4;
const bdateW = (t: string) => Math.ceil(textWidth(t, 10)) + 2;
import { PcLink } from "../../components/PcLink.jsx";

type Mode = "site" | "vendor";
const MODE_KEY = "gb.sch.ovMode";
const VIEW_KEY = "gb.sch.ovView";
const LATE_KEY = "gb.sch.ovLate";
const read = <T extends string>(key: string, ok: readonly T[], def: T): T => {
  try {
    const v = window.localStorage.getItem(key) as T | null;
    return v && ok.includes(v) ? v : def;
  } catch {
    return def;
  }
};
const write = (key: string, v: string) => {
  try {
    window.localStorage.setItem(key, v);
  } catch {
    /* 覚えられなくても使える */
  }
};
const toYmd = (start: string, n: number) => addDays(start, n - 1);
/** 「10/14(水)・10/15(木)」（3日以上続けば「10/14(水)〜10/16(金)」） */
/** 会社の設定「進捗を％で入力」（#292。前の答えに無ければ％） */
const pctOn = (ov: Pick<ScheduleOverview, "company">): boolean => ov.company.progressPct ?? true;
/** 行の進み（吹き出し・スマホのカード）：％ ON は「進捗 50%」、OFF は「状況 着手」 */
const progressText = (ov: Pick<ScheduleOverview, "company">, r: Pick<ScheduleOverviewRow, "progress" | "actualStart">): string =>
  pctOn(ov) ? `進捗 ${r.progress ?? 0}%` : `${SCHEDULE_MSG.stateColumn} ${TASK_STATE_LABEL[taskStateOf(r)]}`;

export function daysLabel(days: readonly string[]): string {
  return dateRuns(days)
    .map((r) => (r.dates.length === 1 ? mdwOf(r.from) : r.dates.length === 2 ? `${mdwOf(r.from)}・${mdwOf(r.to)}` : `${mdwOf(r.from)}〜${mdwOf(r.to)}`))
    .join("・");
}

/* ---------------- 材料（重なり・人数・暦）。紙（#104）と同じものを contract の overviewRowsOf で ---------------- */
type RowInfo = OverviewRowInfo;
type Model = {
  ov: ScheduleOverview;
  companyCal: WorkCalendar;
  rows: RowInfo[];
  byRow: Map<string, RowInfo>;
  overlaps: OverlapResult;
  partnerOf: Map<string, { name: string; fullName: string; kana: string; linkExpiresAt?: string | null }>;
};
function useModel(ov: ScheduleOverview | undefined): Model | null {
  return useMemo(() => {
    if (!ov) return null;
    const { companyCal, rows, overlaps } = overviewRowsOf(ov);
    return { ov, companyCal, rows, byRow: new Map(rows.map((x) => [x.row.id, x])), overlaps, partnerOf: new Map(ov.partners.map((p) => [p.id, p])) };
  }, [ov]);
}
const assigneeName = (m: Model, r: ScheduleOverviewRow): string => overviewAssigneeName(m.partnerOf, r);
/** 名前の右・下の段の小さい印（.sch-chip.sm）の幅：文字（14px）＋左右の padding 16 ＋枠 2 ＋前のすき間 6 ＋アイコン（あれば 14＋すき間 4） */
const chipW = (text: string, icon: number) => textWidth(text, 14) + 16 + 2 + 6 + icon;

/* ============================================================================ */
export function ScheduleOverviewPage() {
  usePageName(SCHEDULE_MSG.overviewTitle);
  const phone = !useWide(768);
  return phone ? <PhoneOverview /> : <DesktopOverview />;
}

/** 使えない会社（モジュール schedule が OFF＝404 MODULE_OFF。前の 403 OPTION_REQUIRED も）の説明。#142 段5 から営業力も同じ案内 */
function OptionOff({ error }: { error: unknown }) {
  const opt = error instanceof ApiError && (error.code === "MODULE_OFF" || error.code === "OPTION_REQUIRED");
  return (
    <div className="paper sch-empty" data-testid="sch-option">
      <p style={{ margin: "0 0 12px" }}>{opt ? SCHEDULE_MSG.optionOff : messageOf(error)}</p>
      {opt ? (
        <PcLink className="btn" to="/account/plan">
          料金プランへ
        </PcLink>
      ) : null}
    </div>
  );
}

/* ============================================================================
 * PC・タブレット（C3・C4）
 * ========================================================================== */
const SITE_H = 52;
/** スマホの「この先の工程」の日数（10/1） */
const UPCOMING_DAYS = 7;
const TASK_H = 46;
const LANE_H = 34;
const SEC_H = 30;

function DesktopOverview() {
  const L = useLabels();
  // 人の列（#297）：ON にした人だけ「今週 延べ」と下の「その日の人数」（案件の工程表の ⋮「表示設定」の「人」と同じ値）
  const [crewOn] = useCrewColumn();
  const nav = useNavigate();
  const { toast, node: toastNode } = useToast();
  const reportsWait = usePendingReports().isPending;
  const [linkFor, setLinkFor] = useState<{ id: string; name: string } | null>(null);
  // 出力（#104、docs/39 §7.2 の10）：いまの並べ方・探す・開いている現場・見ている期間
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const today = useMemo(() => todayLocal(), []);
  const [mode, setModeState] = useState<Mode>(() => read(MODE_KEY, ["site", "vendor"] as const, "site"));
  // 覚えた表示（#291 §2.1.1：前の値 week は旬と読む）
  const [view, setViewState] = useState<View>(() => viewOf(read(VIEW_KEY, ["day", "jun", "month", "week"] as const, "day")) ?? "day");
  const [dayPx, setDayPx] = useState<number | null>(null);
  const setMode = (m: Mode) => (setModeState(m), write(MODE_KEY, m));
  const setView = (v: View) => (setViewState(v), write(VIEW_KEY, v), setDayPx(null));
  // 遅延工程も表示（10/1）：期間より前に終わるはずの遅れている工程も行に。その人のブラウザに覚える
  const [showLate, setShowLateState] = useState(() => read(LATE_KEY, ["1", "0"] as const, "0") === "1");
  const setShowLate = (on: boolean) => (setShowLateState(on), write(LATE_KEY, on ? "1" : "0"));
  const [anchor, setAnchor] = useState(today);
  const [q0, setQ] = useState("");
  const q = q0.trim().toLowerCase();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [secOpen, setSecOpen] = useState<Record<string, boolean>>({ p: true, u: true, t: true, n: true });
  const [pop, setPop] = useState<{ kind: "overlap" | "bar"; anchor: HTMLElement; person?: Person; bars?: VBar[]; rowId?: string } | null>(null);
  const px = view === "day" && dayPx ? dayPx : PX[view];
  const range = useMemo(() => overviewRangeOf(anchor, view), [anchor, view]);
  const from = range.start;
  const to = toYmd(range.start, range.days);
  // 期間の後ろの工程も（ahead=1）・進行中とこれからの現場は過ぎた工程も全部（running=1。先の月へ送っても消えない）。10/1
  // 出力のダイアログの見本は今のまま期間の工程だけ（Export.tsx）
  const oq = useQuery({
    queryKey: ["schedule-overview", from, to, "screen", showLate],
    queryFn: () => api<ScheduleOverview>("GET", `/schedule/overview?from=${from}&to=${to}&ahead=1&running=1${showLate ? "&late=1" : ""}`),
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const m = useModel(oq.data);
  const scroller = useRef<HTMLDivElement>(null);

  /* ---------------- 人（業者ごと） ---------------- */
  const people = useMemo(() => (m ? peopleOf(m, q) : []), [m, q]);
  const sites = useMemo(() => (m ? overviewSitesOf(m.ov, q) : []), [m, q]);

  /* ---------------- 左の列の幅（中身に合わせる 200〜320px） ---------------- */
  const textSize = useTextSize();
  const leftW = useMemo(() => {
    if (!m) return 240;
    const items =
      mode === "site"
        ? m.ov.schedules.flatMap((it) => [
            { text: it.groupName, size: 16, bold: true, extra: 48 }, // 余白 18・▸ 28・すき間 6・線 1（10/1 名前が少し切れていた）
            { text: [it.clientName, it.staffName].filter(Boolean).join("・"), size: 14, extra: 48 },
            ...(open.has(it.scheduleId)
              ? it.rows.flatMap((r) => [
                  { text: r.name || "（名前なし）", size: 15, bold: true, extra: 56 },
                  { text: assigneeName(m, r), size: 14, extra: 56 },
                ])
              : []),
          ])
        : people.flatMap((p) => [
            // 名前の右の印（自社・リンク）の幅も数える（10/1：リンクが列の外で切れていた）。下の段（n現場・重なり・遅れ）も
            { text: p.name, size: 16, bold: true, extra: 24 + (p.kind === "u" ? chipW(SCHEDULE_MSG.selfMark, 0) : p.kind === "p" ? chipW(linkChipText(m.partnerOf.get(p.key.slice(2))?.linkExpiresAt ?? null), 18) : 0) },
            {
              text: SCHEDULE_MSG.sites(p.groups),
              size: 14,
              extra: 24 + (p.overlapDays.length ? chipW(SCHEDULE_MSG.overlapDays(p.overlapDays.length), 0) : 0) + (showLate && p.bars.some((b) => b.late > 0) ? chipW(SCHEDULE_MSG.lateCount(p.bars.filter((b) => b.late > 0).length), 0) : 0),
            },
          ]);
    return nameColumnWidth(items, undefined, { min: 200, max: 320 });
  }, [m, mode, open, people, showLate, textSize]); // textSize：文字の大きさを変えたら測り直す（#115）

  /* ---------------- 時間軸 ---------------- */
  const days = useMemo(() => daysOf(range.start, range.days), [range.start, range.days]);
  // 時間軸の物差し（#291 §2.1.1：旬は1日の幅が旬の日数で変わる）
  const sc = useMemo(() => scaleOf(view, range.start, range.days, px), [view, range.start, range.days, px]);
  const width = sc.total;
  const ix = (d: string) => dayNo(d) - dayNo(range.start);
  const x = (d: string) => sc.x(ix(d));
  const ax: Axis = { x, xr: (d) => sc.x(ix(d) + 1), w: (d) => sc.w(ix(d)) };
  // 旬・月の表示（#291 §2.1.1）：休みを塗らない・バーを1本・会社の休みの帯（案件の{schedule}と同じ）
  const coarse = !(view === "day" && px >= 20);
  // 縦線と見出しの目盛：日を縮めたときは旬の目盛
  const lview: View = coarse ? (view === "month" ? "month" : "jun") : "day";
  // バーの両端の日付は旬・月の表示だけ（全体の{schedule}は選んだ行が無い）
  const dates = view !== "day";
  const [leftDay, setLeftDay] = useState(today);
  const keep = useRef<string | null>(null);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    // 旬・月の表示（#291 §2.1.1）は月の1日から見せる
    const d = keep.current ?? (view === "day" ? addDays(today, -3) : monthStartOf(today));
    el.scrollLeft = Math.max(0, x(d));
    setLeftDay(ymdOfDayNo(dayNo(range.start) + sc.idxAt(el.scrollLeft)));
  }, [range.start, px, view, !!m]); // eslint-disable-line react-hooks/exhaustive-deps
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const d = ymdOfDayNo(dayNo(range.start) + sc.idxAt(el.scrollLeft));
    keep.current = d;
    setLeftDay(d);
  };
  const vwPx = Math.max(px, (scroller.current?.clientWidth ?? 900) - leftW);
  const rightDay = ymdOfDayNo(dayNo(range.start) + Math.max(ix(leftDay), sc.idxAt(x(leftDay) + vwPx) - 1));
  const vwDays = dayNo(rightDay) - dayNo(leftDay) + 1;
  /** ‹ ›：枠の中で動かし、端まで来たら期間を読み直す */
  const step = (dir: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    const n = STEP_DAYS[view];
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    if ((dir < 0 && atStart) || (dir > 0 && atEnd)) {
      keep.current = addDays(leftDay, dir * n);
      setAnchor((a) => addDays(a, dir * n));
      return;
    }
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollBy({ left: dir * n * px, behavior: reduce ? "auto" : "smooth" });
  };
  const toToday = () => {
    const at = view === "day" ? addDays(today, -3) : monthStartOf(today);
    keep.current = at;
    if (anchor !== today) setAnchor(today);
    else if (scroller.current) scroller.current.scrollLeft = Math.max(0, x(at));
  };
  /** 端の印（10/1）：その日が真ん中に来るまで表を送る。読んだ期間の外なら、その日のまわりを読み直す */
  const goTo = (d: string) => {
    const el = scroller.current;
    if (!el) return;
    const left = addDays(d, -Math.floor(vwDays / 2));
    if (d >= from && d <= to) {
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollTo({ left: Math.max(0, x(left)), behavior: reduce ? "auto" : "smooth" });
      return;
    }
    keep.current = left;
    setAnchor(d);
  };
  const lateChip = (n: number | undefined) =>
    showLate && n ? (
      <span className="sch-chip shu sm" data-testid="sch-ov-late">
        {SCHEDULE_MSG.delayBadge(n)}
      </span>
    ) : null;
  const onZoom = useCallback((n: number) => {
    setViewState("day");
    setDayPx(n);
  }, []);
  usePinchZoom(scroller, px, onZoom, leftW);

  /* ---------------- しるし ---------------- */
  const running = m ? m.ov.schedules.filter((it) => it.start && it.end && it.start <= today && today <= it.end).length : 0;
  const mon = mondayOf(today);
  const weekDays = new Set(Array.from({ length: 7 }, (_, i) => addDays(mon, i)));
  const weekCovered = m ? m.ov.from <= mon && addDays(mon, 6) <= m.ov.to : false;
  const weekCrew = m && weekCovered ? [...crewByDate(m.rows.filter((r) => r.row.kind === "task").map((r) => ({ dates: r.dates.filter((d) => weekDays.has(d)), crew: r.row.crew })))].reduce((n, [, c]) => n + c, 0) : 0;
  const overlapN = m?.overlaps.count ?? 0;
  const jumpOverlap = () => {
    setMode("vendor");
    setSecOpen((s) => ({ ...s, p: true, u: true }));
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[data-testid="sch-ov-person"][data-overlap="1"]');
      el?.scrollIntoView({ block: "center" });
      el?.querySelector<HTMLElement>('[data-testid="sch-ov-overlap"]')?.focus();
    });
  };

  /* ---------------- その日の人数（見えている行の分だけ） ---------------- */
  const crew = useMemo(() => {
    if (!m) return new Map<string, number>();
    const src =
      mode === "site"
        ? sites.flatMap((it) => it.rows.filter((r) => r.kind === "task").map((r) => m.byRow.get(r.id)!))
        : people.flatMap((p) => p.bars.map((b) => m.byRow.get(b.id)!));
    return crewByDate(src.map((r) => ({ dates: r.dates, crew: r.row.crew })));
  }, [m, mode, sites, people]);
  const crewMax = Math.max(1, ...crew.values());

  if (oq.isPending) return <p className="hint">読み込み中…</p>;
  if (oq.isError && !oq.data) {
    return (
      <div className="sch-page full ov">
        <div className="sch-ovtitle">
          <h1>{SCHEDULE_MSG.overviewTitle}</h1>
        </div>
        <OptionOff error={oq.error} />
      </div>
    );
  }
  if (!m) return null;
  const ov = m.ov;

  const bodyRows: ReactNode[] = [];
  if (mode === "site") {
    // 案件なしの{schedule}（#291）は一番上の段にまとめる（宙に浮いたものを出し続ける。期間に行が無くても出る）
    const loose = sites.filter((it) => !it.groupId);
    const order: (ScheduleOverviewItem | "loose")[] = [...(loose.length ? (["loose"] as const) : []), ...(secOpen.s ?? true ? loose : []), ...sites.filter((it) => !!it.groupId)];
    for (const it of order) {
      if (it === "loose") {
        const on = secOpen.s ?? true;
        bodyRows.push(
          <div key="sec-loose" className="sch-ovrow sec" style={{ height: SEC_H }}>
            <div className="sch-ovleft" style={{ width: leftW }}>
              <button type="button" className="sch-ovsec" aria-expanded={on} onClick={() => setSecOpen((x) => ({ ...x, s: !on }))} data-testid="sch-ov-sec-loose">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={on ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"} />
                </svg>
                {t(SCHEDULE_MSG.standaloneSection(loose.length))}
              </button>
            </div>
            <div className="sch-ovt" style={{ width }} />
          </div>,
        );
        continue;
      }
      const isOpen = open.has(it.scheduleId);
      const siteOverlap = it.rows.some((r) => m.overlaps.byRow.has(r.id));
      const s = it.start && it.start < ov.from ? ov.from : it.start;
      const e = it.end && it.end > ov.to ? ov.to : it.end;
      // 期間の後ろにしか無い現場・期間より前に終わるはずの現場（遅延工程も表示）は読んだ期間にバーが無い＝端の印だけ
      const siteDrawn = !!s && !!e && s <= e;
      const siteEdge = it.start && it.end ? edgeMarksOf([{ id: it.scheduleId, start: it.start, end: it.end }], leftDay, rightDay) : { before: null, after: null };
      const siteChips = (
        <>
          {it.lateDays > 0 ? <span className="sch-chip shu">{SCHEDULE_MSG.delayBadge(it.lateDays)}</span> : null}
          {siteOverlap ? (
            <span className="sch-chip shu" data-testid="sch-ov-site-overlap">
              {SCHEDULE_MSG.overlap}
            </span>
          ) : null}
        </>
      );
      bodyRows.push(
        <div key={it.scheduleId} className={"sch-ovrow site" + (it.groupId ? "" : " loose")} style={{ height: SITE_H }} data-testid="sch-ov-site" data-group-id={it.groupId ?? ""} data-schedule-id={it.scheduleId}>
          <div className="sch-ovleft" style={{ width: leftW }}>
            <button
              type="button"
              className="sch-fold"
              aria-expanded={isOpen}
              aria-label={t(isOpen ? `「${it.groupName}」の{task}を折りたたみ` : `「${it.groupName}」の{task}を展開`)}
              onClick={() => setOpen((o) => (o.has(it.scheduleId) ? new Set([...o].filter((g) => g !== it.scheduleId)) : new Set([...o, it.scheduleId])))}
              data-testid="sch-ov-fold"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={isOpen ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"} />
              </svg>
            </button>
            <span className="sch-ovtwo">
              <Link to={scheduleHref(it)} title={it.groupName}>
                <b>{it.groupName}</b>
              </Link>
              <small>{[it.clientName, it.staffName].filter(Boolean).join("・")}</small>
            </span>
          </div>
          <div className="sch-ovt" style={{ width }}>
            {siteDrawn ? (
              <>
                <span className="sch-ovsite" style={{ left: x(s!), width: ax.xr(e!) - x(s!), ["--done" as string]: `${it.progress ?? 0}%` }} aria-hidden="true" />
                <span className="sch-ovafter" style={{ left: ax.xr(e!) + 8 }}>
                  {/* #292：％ OFF の会社は「完了 n／m{task}」（progress は API が完了した{task}の日数の割合で返す） */}
                  <span className="pct" data-testid="sch-ov-site-progress">{!pctOn(ov) && it.taskCount != null ? (it.taskCount ? SCHEDULE_MSG.doneOf(it.doneCount ?? 0, it.taskCount) : "") : it.progress == null ? "" : `${it.progress}%`}</span>
                  {siteChips}
                </span>
              </>
            ) : null}
            {siteEdge.before ? <EdgeMark side="before" date={siteEdge.before.end} name={it.groupName} onGo={goTo} testId="sch-ov-site-edge" extra={siteDrawn ? null : siteChips} /> : null}
            {siteEdge.after ? <EdgeMark side="after" date={siteEdge.after.start} name={it.groupName} onGo={goTo} testId="sch-ov-site-edge" /> : null}
          </div>
        </div>,
      );
      if (!isOpen) continue;
      for (const r of [...it.rows].sort((a, b) => a.position - b.position)) {
        const info = m.byRow.get(r.id)!;
        const ovl = m.overlaps.byRow.get(r.id);
        const rEnd = r.end ?? r.start;
        const drawn = r.start <= ov.to && rEnd >= ov.from;
        const edge = edgeMarksOf([{ id: r.id, start: r.start, end: rEnd }], leftDay, rightDay);
        bodyRows.push(
          <div key={r.id} className="sch-ovrow task" style={{ height: TASK_H }} data-testid="sch-ov-task" data-row-id={r.id}>
            <div className="sch-ovleft in" style={{ width: leftW }}>
              {/* 工程名の下の段に 担当（10/1 射場さん。現場の行の「{顧客}・担当者」と同じ2段。人数の字は #297 でやめた） */}
              <span className="sch-ovtwo">
                <Link to={scheduleHref(it, r.id)} className="nm" title={r.name}>
                  {r.kind === "milestone" ? <span className="sch-dia sm hollow" aria-hidden="true" /> : null}
                  <b>{r.name || "（名前なし）"}</b>
                </Link>
                {assigneeName(m, r) ? <small data-testid="sch-ov-task-sub">{assigneeName(m, r)}</small> : null}
              </span>
            </div>
            <div className="sch-ovt" style={{ width }}>
              {ovl ? ovl.dates.map((d) => <span key={d} className="sch-ovday" style={{ left: x(d), width: ax.w(d) }} aria-hidden="true" />) : null}
              <OvBar info={info} ax={ax} from={ov.from} to={ov.to} overlap={!!ovl} coarse={coarse} dates={dates} pct={pctOn(ov)} onClick={(a) => setPop({ kind: "bar", anchor: a, rowId: r.id })} />
              {drawn && (ovl || lateChip(r.late)) ? (
                <span className="sch-ovafter" style={{ left: ax.xr(rEnd > ov.to ? ov.to : rEnd) + 8 + (dates && r.kind !== "milestone" ? BDATE_GAP + bdateW(mdOf(rEnd > ov.to ? ov.to : rEnd)) : 0) }}>
                  {ovl ? (
                    <button type="button" className="sch-chip shu" onClick={(e) => setPop({ kind: "bar", anchor: e.currentTarget, rowId: r.id })} data-testid="sch-ov-task-overlap">
                      {SCHEDULE_MSG.overlap}
                    </button>
                  ) : null}
                  {lateChip(r.late)}
                </span>
              ) : null}
              {edge.before ? <EdgeMark side="before" date={rEnd} name={r.name} onGo={goTo} extra={lateChip(r.late)} /> : null}
              {edge.after ? <EdgeMark side="after" date={r.start} name={r.name} onGo={goTo} extra={lateChip(r.late)} /> : null}
            </div>
          </div>,
        );
      }
    }
  } else {
    const secs: { k: Person["kind"]; label: string }[] = [
      { k: "p", label: SCHEDULE_MSG.groupVendors },
      { k: "u", label: SCHEDULE_MSG.groupUsers },
      { k: "t", label: SCHEDULE_MSG.groupText },
      { k: "n", label: "" },
    ];
    for (const sec of secs) {
      const list = people.filter((p) => p.kind === sec.k);
      if (!list.length) continue;
      const on = secOpen[sec.k] ?? true;
      const nTasks = list.reduce((n, p) => n + p.bars.length, 0);
      bodyRows.push(
        <div key={`sec-${sec.k}`} className="sch-ovrow sec" style={{ height: SEC_H }}>
          <div className="sch-ovleft" style={{ width: leftW }}>
            <button type="button" className="sch-ovsec" aria-expanded={on} onClick={() => setSecOpen((s) => ({ ...s, [sec.k]: !on }))} data-testid={`sch-ov-sec-${sec.k}`}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={on ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"} />
              </svg>
              {sec.k === "n" ? SCHEDULE_MSG.groupNone(nTasks) : sec.label}
            </button>
          </div>
          <div className="sch-ovt" style={{ width }} />
        </div>,
      );
      if (!on) continue;
      for (const p of list) {
        // 段は読んだ期間にかかるバーだけで分ける（期間の外のバーは端の印。10/1）
        const inRange = p.bars.filter((b) => b.start <= ov.to && b.end >= ov.from);
        const lanes = lanesOf(inRange.map((b) => ({ id: b.id, start: b.start, end: b.end })));
        const h = Math.max(SITE_H, lanes.count * LANE_H + 18);
        const days0 = p.overlapDays;
        const edge = edgeMarksOf(p.bars, leftDay, rightDay);
        const laneTop = (id: string) => 9 + (lanes.lane.get(id) ?? 0) * LANE_H - 4;
        const lateN = showLate ? p.bars.filter((b) => b.late > 0).length : 0;
        bodyRows.push(
          <div key={p.key} className="sch-ovrow person" style={{ height: h }} data-testid="sch-ov-person" data-key={p.key} data-overlap={days0.length ? "1" : "0"}>
            <div className="sch-ovleft" style={{ width: leftW }}>
              <span className="sch-ovtwo">
                {/* 名前だけを省略記号で切り、右の印（自社・リンク）はいつも見える（10/1：長い名前で「リンク」が列の外で切れていた） */}
                <span className="sch-ovname">
                  <b title={p.full || p.name} data-testid="sch-ov-person-name">
                    {p.name}
                  </b>
                  {p.kind === "u" ? <span className="sch-chip ai sm">{SCHEDULE_MSG.selfMark}</span> : null}
                  {/* 取引先（業者・{顧客}）の行：リンクを送る（#103 §6.3.5） */}
                  {p.kind === "p" ? (
                    <button
                      type="button"
                      className={"sch-chip sm sch-linkchip" + (m.partnerOf.get(p.key.slice(2))?.linkExpiresAt ? " on" : "")}
                      aria-label={PARTNER_LINK_MSG.menuSendLink(p.name)}
                      onClick={() => setLinkFor({ id: p.key.slice(2), name: p.name })}
                      data-testid="sch-ov-link"
                    >
                      <CrIcon k="link" />
                      {linkChipText(m.partnerOf.get(p.key.slice(2))?.linkExpiresAt ?? null)}
                    </button>
                  ) : null}
                </span>
                <small>
                  {SCHEDULE_MSG.sites(p.groups)}
                  {lateN ? (
                    <span className="sch-chip shu sm" data-testid="sch-ov-person-late">
                      {SCHEDULE_MSG.lateCount(lateN)}
                    </span>
                  ) : null}
                  {days0.length ? (
                    <button type="button" className="sch-chip shu sm" onClick={(e) => setPop({ kind: "overlap", anchor: e.currentTarget, person: p, bars: p.bars.filter((b) => b.overlap.length) })} data-testid="sch-ov-overlap">
                      {SCHEDULE_MSG.overlapDays(days0.length)}
                    </button>
                  ) : null}
                </small>
              </span>
            </div>
            <div className="sch-ovt" style={{ width }}>
              {days0.map((d) => (
                <span key={d} className="sch-ovday" style={{ left: x(d), width: ax.w(d) }} aria-hidden="true" />
              ))}
              {inRange.map((b) => {
                const lane = lanes.lane.get(b.id) ?? 0;
                const same = inRange.filter((o) => (lanes.lane.get(o.id) ?? 0) === lane && o.start > b.start).sort((a2, b2) => (a2.start < b2.start ? -1 : 1))[0];
                return (
                  <VBarView
                    coarse={coarse}
                    pct={pctOn(ov)}
                    key={b.id}
                    b={b}
                    top={9 + lane * LANE_H}
                    ax={ax}
                    from={ov.from}
                    to={ov.to}
                    dates={dates}
                    nextX={same ? x(same.start < ov.from ? ov.from : same.start) - (dates && same.start >= ov.from ? BDATE_GAP + bdateW(mdOf(same.start)) : 0) : null}
                    onClick={(a) => (b.overlap.length ? setPop({ kind: "overlap", anchor: a, person: p, bars: overlapGroupOf(p, b) }) : setPop({ kind: "bar", anchor: a, rowId: b.id }))}
                  />
                );
              })}
              {edge.before ? <EdgeMark side="before" date={edge.before.end} name={`${edge.before.groupName} ${edge.before.name}`} onGo={goTo} top={laneTop(edge.before.id)} extra={lateChip(edge.before.late)} /> : null}
              {edge.after ? <EdgeMark side="after" date={edge.after.start} name={`${edge.after.groupName} ${edge.after.name}`} onGo={goTo} top={laneTop(edge.after.id)} /> : null}
            </div>
          </div>,
        );
      }
    }
  }

  const empty = ov.schedules.length === 0;
  const project = L.project;
  return (
    <div className="sch-page full ov" data-testid="sch-overview">
      <div className="sch-ovtitle">
        <h1>{SCHEDULE_MSG.overviewTitle}</h1>
        {/* 0 のときは出さない（#103 §6.1 の2。「重なり」「延べ」と同じ） */}
        {running ? (
          <span className="sch-chip" data-testid="sch-ov-running">
            {SCHEDULE_MSG.running(running)}
          </span>
        ) : null}
        {crewOn && weekCrew ? (
          <span className="sch-chip" data-testid="sch-ov-weekcrew">
            {SCHEDULE_MSG.weekCrew(weekCrew)}
          </span>
        ) : null}
        {overlapN ? (
          <button type="button" className="sch-chip shu" onClick={jumpOverlap} data-testid="sch-ov-overlaps">
            {SCHEDULE_MSG.overlapCount(overlapN)}
          </button>
        ) : null}
        <span className="sp" />
        <ReportHistoryButton />
        {/* 案件なしの{schedule}（#291）：「出力」の左 */}
        <button type="button" className="btn" onClick={() => setCreating(true)} data-testid="sch-ov-create">
          <CrIcon k="plus" />
          {SCHEDULE_MSG.create}
        </button>
        <button type="button" className="btn" onClick={() => setExporting(true)} data-testid="sch-ov-export">
          <CrIcon k="download" />
          {SCHEDULE_EXPORT_MSG.button}
        </button>
      </div>
      <div className="sch-ops" role="toolbar" aria-label={t("全体の{schedule}の操作")}>
        <div className="sch-seg" role="radiogroup" aria-label="並べ方">
          {(
            [
              ["site", SCHEDULE_MSG.bySite],
              ["vendor", SCHEDULE_MSG.byVendor],
            ] as const
          ).map(([k, t]) => (
            <button key={k} type="button" role="radio" aria-checked={mode === k} className={mode === k ? "on" : ""} onClick={() => setMode(k)} data-testid={`sch-ov-mode-${k}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="sch-seg" role="radiogroup" aria-label="表示">
          {(
            [
              ["day", "日"],
              ["jun", "旬"],
              ["month", "月"],
            ] as const
          ).map(([k, t]) => (
            <button key={k} type="button" role="radio" aria-checked={view === k} className={view === k ? "on" : ""} onClick={() => setView(k)} title={k === "jun" ? SCHEDULE_JUN_TITLE : undefined} data-testid={`sch-view-${k}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="sch-nav">
          <button type="button" className="btn quiet sq" aria-label="前へ" onClick={() => step(-1)} data-testid="sch-ov-prev">
            <CrIcon k="back" />
          </button>
          <button type="button" className="btn quiet" onClick={toToday} data-testid="sch-today-btn">
            今日
          </button>
          <button type="button" className="btn quiet sq" aria-label="次へ" onClick={() => step(1)} data-testid="sch-ov-next">
            <CrIcon k="right" />
          </button>
        </div>
        <span className="sch-period" data-testid="sch-period">
          <span className="long">
            {mdwLongOf(leftDay)}〜 {mdwLongOf(rightDay)}
          </span>
          <span className="short" aria-hidden="true">
            {mdwOf(leftDay)}〜{mdwOf(rightDay)}
          </span>
        </span>
        <span className="sp" />
        <label className="chk sch-ovcheck">
          <input type="checkbox" checked={showLate} onChange={(e) => setShowLate(e.target.checked)} data-testid="sch-ov-late-toggle" />
          {SCHEDULE_MSG.showLate}
        </label>
        <label className="sch-ovsearch">
          <CrIcon k="search" />
          <input type="search" value={q0} onChange={(e) => setQ(e.target.value)} placeholder={SCHEDULE_MSG.search} aria-label={SCHEDULE_MSG.search} data-testid="sch-ov-search" />
        </label>
      </div>
      {/* 協力会社からの報告（R1。確かめ待ちがあるときだけ）。届くまで表は見えない形で場所だけ取る（#179：欄が後から入ると表がずれて押し間違える） */}
      <ReportsPanel toast={toast} />
      <div className="paper sch-card" data-testid="sch-card" aria-busy={reportsWait || undefined} style={reportsWait ? { visibility: "hidden" } : undefined}>
        {empty ? (
          <div className="sch-ovempty" data-testid="sch-ov-empty">
            <p>{SCHEDULE_MSG.overviewEmpty(project)}</p>
            <Link className="btn" to="/groups">
              {project}一覧へ
            </Link>
          </div>
        ) : null}
        <div className="sch-scroll" ref={scroller} onScroll={onScroll} data-testid="sch-scroll" hidden={empty}>
          <div className="sch-inner ov" style={{ width: leftW + width, ["--left" as string]: `${leftW}px` }}>
            <OvHeader label={t(mode === "site" ? "{siteShort}・{task}" : "{contractor}・担当")} days={days} sc={sc} hview={lview} cal={m.companyCal} today={today} leftW={leftW} leftIdx={Math.max(0, dayNo(leftDay) - dayNo(range.start))} />
            <div className="sch-ovbody">
              <div className="sch-bg" aria-hidden="true" style={{ left: leftW, width }}>
                {/* 旬・月の表示（#291 §2.1.1・#290 ヲ）：案件の{schedule}と同じ＝休みを塗らず、会社の休みは帯と名前 */}
                {coarse
                  ? ov.company.offDays.map((o) =>
                      o.to < days[0]! || o.from > days[days.length - 1]! ? null : (
                        <span key={`${o.from}`} className="sch-offband" style={{ left: x(o.from < days[0]! ? days[0]! : o.from), width: ax.xr(o.to > days[days.length - 1]! ? days[days.length - 1]! : o.to) - x(o.from < days[0]! ? days[0]! : o.from) }} data-testid="sch-offband" data-kind="off" title={o.name}>
                          {o.name ? <b>{o.name}</b> : null}
                        </span>
                      ),
                    )
                  : px >= 4
                    ? days.map((d, i) => (isOffDay(d, m.companyCal) ? <span key={d} className="sch-off" style={{ left: sc.x(i), width: sc.w(i) }} /> : null))
                    : null}
                <ScaleLines view={lview} days={days} sc={sc} />
                {today >= ov.from && today <= ov.to ? <span className="sch-today" style={{ left: x(today) + ax.w(today) / 2 - 1 }} /> : null}
              </div>
              {bodyRows}
              {!bodyRows.length && !empty ? <p className="hint sch-none">{t("該当する{contractor}・{siteShort}はありません")}</p> : null}
              {/* 行の下の余りも左の列で覆う（横に流した休みの地色が左の列の下に見えないように） */}
              <div className="sch-ovrow fill" aria-hidden="true">
                <div className="sch-ovleft" style={{ width: leftW }} />
              </div>
            </div>
            {/* 「その日の人数」は 人の列を ON にした人だけ（#297） */}
            {crewOn ? (
              <div className="sch-ovfoot" data-testid="sch-ov-crew" data-notice-above="">
                <div className="sch-ovleft" style={{ width: leftW }}>
                  <span className="sch-ovtwo">
                    <b>{SCHEDULE_MSG.crewDay}</b>
                    <small>{SCHEDULE_MSG.crewDaySub}</small>
                  </span>
                </div>
                <div className="sch-ovt" style={{ width }}>
                  {view === "day" && px >= 20
                    ? days.map((d, i) => {
                        const n = crew.get(d) ?? 0;
                        return n ? (
                          <span key={d} className="sch-ovcrew" style={{ left: sc.x(i), width: sc.w(i) }} data-date={d} data-testid="sch-ov-crew-day">
                            <b>{n}</b>
                            <i style={{ height: Math.max(3, Math.round((n / crewMax) * 12)) }} />
                          </span>
                        ) : null;
                      })
                    : junTicks(days).map((u) => {
                        // 旬・月の表示（#291 §2.1.1）：旬ごとの延べの人数
                        let n = 0;
                        for (let k = 0; k < u.n; k++) n += crew.get(days[u.i + k]!) ?? 0;
                        return n ? (
                          <span key={days[u.i]} className="sch-ovcrew wk" style={{ left: sc.x(u.i) }} data-date={days[u.i]} data-testid="sch-ov-crew-week">
                            {SCHEDULE_MSG.weekTotal(n)}
                          </span>
                        ) : null;
                      })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {/* 表示できる数を超えた（#171）：一覧の下に1行の注記（前は表の上の1行。帯にしない） */}
      {ov.truncated ? (
        <p className="hint sch-ovnote" role="note" data-testid="sch-ov-truncated">
          {SCHEDULE_MSG.overviewTruncated}
        </p>
      ) : null}
      {pop?.kind === "overlap" && pop.person && pop.bars ? (
        <OverlapPop person={pop.person} bars={pop.bars} anchor={pop.anchor} onClose={() => setPop(null)} onOpen={(b) => nav(scheduleHref(b, b.id))} />
      ) : null}
      {pop?.kind === "bar" && pop.rowId && m.byRow.get(pop.rowId) ? <BarPop m={m} info={m.byRow.get(pop.rowId)!} anchor={pop.anchor} onClose={() => setPop(null)} /> : null}
      {linkFor ? <PartnerLinkDialog partnerId={linkFor.id} partnerName={linkFor.name} onClose={() => setLinkFor(null)} toast={(t) => toast(t)} /> : null}
      {exporting ? <ExportDialog target={{ kind: "overview", mode, q: q0, open: [...open], view: { from: leftDay, to: rightDay } }} onClose={() => setExporting(false)} toast={(t) => toast(t)} /> : null}
      {creating ? <CreateScheduleDialog onCancel={() => setCreating(false)} /> : null}
      {toastNode}
    </div>
  );
}

/* ---------------- 見出し（月／日・曜日。地の休みは会社の休み） ---------------- */
function OvHeader({ label, days, sc, hview, cal, today, leftW, leftIdx }: { label: string; days: string[]; sc: ReturnType<typeof scaleOf>; hview: View; cal: WorkCalendar; today: string; leftW: number; leftIdx: number }) {
  const months: { ym: string; from: number; n: number }[] = [];
  days.forEach((d, i) => {
    const ym = d.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.ym === ym) last.n++;
    else months.push({ ym, from: i, n: 1 });
  });
  const dayMode = hview === "day";
  return (
    <div className={"sch-head" + (dayMode ? "" : " coarse") + (hview === "month" ? " month" : "")}>
      <div className="sch-hl ov" style={{ width: leftW }}>
        <span className="c-name">{label}</span>
      </div>
      <div className="sch-ht" style={{ width: sc.total }}>
        <div className="sch-months">
          {months.map((mm, k) => (
            <span key={mm.ym} style={{ left: sc.x(mm.from), width: sc.x(mm.from + mm.n) - sc.x(mm.from) }}>
              {sc.x(mm.from + mm.n) - sc.x(Math.max(mm.from, leftIdx)) >= (k === 0 || mm.ym.endsWith("-01") ? 100 : 44) ? (
                <b>{k === 0 || mm.ym.endsWith("-01") ? `${Number(mm.ym.slice(0, 4))}年 ${Number(mm.ym.slice(5))}月` : `${Number(mm.ym.slice(5))}月`}</b>
              ) : null}
            </span>
          ))}
        </div>
        {hview === "month" ? null : (
          <div className="sch-days">
            {dayMode
              ? days.map((d, i) => {
                  // 曜日の色（#291 §2.1.1）：土＝青・日祝＝朱
                  const wd = weekdayOf(d);
                  const hol = cal.holidays.has(d);
                  return (
                    <span key={d} className={"d" + (isOffDay(d, cal) ? " off" : "") + (hol || wd === 0 ? " sun" : wd === 6 ? " sat" : "") + (d === today ? " today" : "")} style={{ left: sc.x(i), width: sc.w(i) }} data-date={d}>
                      <b>{Number(d.slice(8))}</b>
                      <i>{hol ? "祝" : WEEKDAY_JA[wd]}</i>
                    </span>
                  );
                })
              : <JunTicks days={days} sc={sc} />}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- 現場ごとの工程のバー（休みで区切り、済んだ所は濃い） ---------------- */
/**
 * 端の印（10/1）：見えている日の外にあるバーを、表の端に「12/12〜 ›」「‹ 〜10/2」で知らせる。押すとその日まで表を送る。
 * 横に送っても端に残る（position: sticky。左は左の列の幅のぶん内側）。extra は印の横に並べるもの（遅れ・重なり）
 */
function EdgeMark({ side, date, name, onGo, top, extra, testId = "sch-ov-edge" }: { side: "before" | "after"; date: string; name: string; onGo: (d: string) => void; top?: number; extra?: ReactNode; testId?: string }) {
  const md = mdOf(date);
  const before = side === "before";
  return (
    <div className={"sch-ovedge " + side} style={top != null ? { top, bottom: "auto", height: LANE_H } : undefined}>
      <span className="pin">
        {before ? null : extra}
        <button type="button" className="sch-chip sch-edge" aria-label={before ? SCHEDULE_MSG.edgeBeforeLabel(name, md) : SCHEDULE_MSG.edgeAfterLabel(name, md)} onClick={() => onGo(date)} data-testid={testId} data-side={side} data-date={date}>
          {before ? <CrIcon k="back" /> : null}
          {before ? SCHEDULE_MSG.edgeBefore(md) : SCHEDULE_MSG.edgeAfter(md)}
          {before ? null : <CrIcon k="right" />}
        </button>
        {before ? extra : null}
      </span>
    </div>
  );
}

function OvBar({ info, ax, from, to, overlap, coarse = false, dates = false, pct = true, onClick }: { info: RowInfo; ax: Axis; from: string; to: string; overlap: boolean; coarse?: boolean; dates?: boolean; pct?: boolean; onClick: (a: HTMLElement) => void }) {
  const { x } = ax;
  const r = info.row;
  if (r.kind === "milestone") {
    if (r.start < from || r.start > to) return null;
    return (
      <button type="button" className={"sch-ovms" + ((r.progress ?? 0) >= 100 ? " done" : "")} style={{ left: x(r.start) + ax.w(r.start) / 2 - 8 }} onClick={(e) => onClick(e.currentTarget)} aria-label={`${r.name} ${mdOf(r.start)}`} data-testid="sch-ov-bar">
        <span className="dia" />
        <span className="lbl">{r.name}</span>
      </button>
    );
  }
  const s = r.start < from ? from : r.start;
  const e0 = r.end ?? r.start;
  const e = e0 > to ? to : e0;
  if (s > e) return null;
  const runs0 = workRuns(s, e, calendarForRow({ kind: "task", id: r.id, workOffDays: r.workOffDays }, info.cal));
  // 旬・月の表示（#291 §2.1.1）：休みで区切らず1本（済んだ所は暦の日に直す）
  // 済んだ所（#292：％ OFF は 完了＝全部・着手＝半分・未着手＝無し）
  const dp = barDonePct(r, pct);
  const j = coarse && runs0.length > 1 ? joinedRun(runs0, dp) : null;
  const runs = j ? [{ from: j.from, to: j.to }] : runs0;
  const total = runs0.reduce((n, u) => n + dayNo(u.to) - dayNo(u.from) + 1, 0) || 1;
  let doneLeft = j ? j.done : (total * dp) / 100;
  const l = x(s);
  const w = ax.xr(e) - l;
  return (
    <>
    <button
      type="button"
      className={"sch-bar ov" + ((r.progress ?? 0) >= 100 ? " done" : "") + (overlap ? " ovl" : "")}
      style={{ left: l, width: w }}
      onClick={(ev) => onClick(ev.currentTarget)}
      aria-label={`${r.name} ${mdOf(r.start)}〜${mdOf(e0)} ${r.days ?? 1}日`}
      data-testid="sch-ov-bar"
      data-row-id={r.id}
    >
      {runs.map((u, k) => {
        const n = dayNo(u.to) - dayNo(u.from) + 1;
        const f = Math.max(0, Math.min(n, doneLeft)) / n;
        doneLeft -= n;
        const prev = runs[k - 1];
        return (
          <Fragment key={u.from}>
            {prev ? <span className="gap" style={{ left: x(addDays(prev.to, 1)) - l, width: x(u.from) - x(addDays(prev.to, 1)) }} /> : null}
            <span className="bseg" style={{ left: x(u.from) - l, width: ax.xr(u.to) - x(u.from), ["--done" as string]: `${Math.round(f * 100)}%` }} />
          </Fragment>
        );
      })}
    </button>
    {/* バーの両端の日付（#291 §2.1.1。期間の外に続くときの端は出さない） */}
    {dates ? <OvBarDates start={r.start >= from ? r.start : null} end={e0 <= to && !(e0 === r.start && r.start >= from) ? e0 : null} l={l} r={l + w} /> : null}
    </>
  );
}

/** バーの両端の日付（全体の{schedule}。#291 §2.1.1）：10px・薄墨。top は業者ごとの段の上端 */
function OvBarDates({ start, end, l, r, top }: { start: string | null; end: string | null; l: number; r: number; top?: number }) {
  const v = top != null ? " v" : "";
  return (
    <>
      {start ? (
        <span className={"sch-bdate l" + v} style={{ left: l - BDATE_GAP - bdateW(mdOf(start)), width: bdateW(mdOf(start)), top }} data-testid="sch-bdate" aria-hidden="true">
          {mdOf(start)}
        </span>
      ) : null}
      {end ? (
        <span className={"sch-bdate r" + v} style={{ left: r + BDATE_GAP, width: bdateW(mdOf(end)), top }} data-testid="sch-bdate" aria-hidden="true">
          {mdOf(end)}
        </span>
      ) : null}
    </>
  );
}

/* ---------------- 業者ごと ---------------- */
type VBar = OverviewPersonBar & { info: RowInfo };
type Person = Omit<OverviewPerson, "bars"> & { bars: VBar[] };
/** 業者ごと：まとめ方・探す・並びは contract の overviewPeopleOf（紙と同じ）。画面はバーに材料（暦）を付けるだけ */
function peopleOf(m: Model, q: string): Person[] {
  return overviewPeopleOf(m.ov, m.rows, m.overlaps, q).map((p) => ({ ...p, bars: p.bars.map((b) => ({ ...b, info: m.byRow.get(b.id)! })) }));
}
/** その重なったバーと、同じ日に重なる相手（同じ人のほかの現場のバー） */
function overlapGroupOf(p: Person, b: VBar): VBar[] {
  const days = new Set(b.overlap);
  return p.bars.filter((o) => o.id === b.id || (o.scheduleId !== b.scheduleId && o.overlap.some((d) => days.has(d))));
}

function VBarView({ b, top, ax, from, to, nextX, coarse = false, dates = false, pct = true, onClick }: { b: VBar; top: number; ax: Axis; from: string; to: string; nextX: number | null; coarse?: boolean; dates?: boolean; pct?: boolean; onClick: (a: HTMLElement) => void }) {
  const { x } = ax;
  const s = b.start < from ? from : b.start;
  const e = b.end > to ? to : b.end;
  if (s > e) return null;
  const l = x(s);
  const w = ax.xr(e) - l;
  const runs0 = workRuns(s, e, calendarForRow({ kind: "task", id: b.id, workOffDays: b.info.row.workOffDays }, b.info.cal));
  // 旬・月の表示（#291 §2.1.1）：休みで区切らず1本
  const dp = barDonePct(b, pct);
  const j = coarse && runs0.length > 1 ? joinedRun(runs0, dp) : null;
  const runs = j ? [{ from: j.from, to: j.to }] : runs0;
  const total = runs0.reduce((n, u) => n + dayNo(u.to) - dayNo(u.from) + 1, 0) || 1;
  let doneLeft = j ? j.done : (total * dp) / 100;
  // バーの両端の日付（#291 §2.1.1）：右は次のバー（とその左の日付）にぶつかれば出さない。現場名が右に出るときはその右に
  const endDate = dates && b.end <= to && !(b.end === b.start && b.start >= from) ? mdOf(b.end) : null;
  const dateW = endDate ? BDATE_GAP + bdateW(endDate) : 0;
  const dateOk = !!endDate && (nextX == null || l + w + dateW <= nextX - 2);
  // 現場名：バーに入れば中、入らなければ右（次のバーにかかるなら出さず title だけ）
  const tw = textWidth(b.groupName, 14, true) + 12;
  const inside = tw <= w;
  const lblX = l + w + 6 + (dateOk ? dateW : 0);
  const rightOk = !inside && (nextX == null || lblX + tw <= nextX - 4);
  return (
    <>
      <button
        type="button"
        className={"sch-bar ov v" + (b.progress >= 100 ? " done" : "") + (b.overlap.length ? " ovl" : "")}
        style={{ left: l, width: w, top }}
        title={`${b.groupName}・${b.name}`}
        onClick={(ev) => onClick(ev.currentTarget)}
        aria-label={`${b.groupName} ${b.name} ${mdOf(b.start)}〜${mdOf(b.end)}${b.overlap.length ? `（${SCHEDULE_MSG.overlap}）` : ""}`}
        data-testid="sch-ov-vbar"
        data-row-id={b.id}
        data-overlap={b.overlap.length ? "1" : "0"}
      >
        {runs.map((u, k) => {
          const n = dayNo(u.to) - dayNo(u.from) + 1;
          const f = Math.max(0, Math.min(n, doneLeft)) / n;
          doneLeft -= n;
          const prev = runs[k - 1];
          return (
            <Fragment key={u.from}>
              {prev ? <span className="gap" style={{ left: x(addDays(prev.to, 1)) - l, width: x(u.from) - x(addDays(prev.to, 1)) }} /> : null}
              <span className="bseg" style={{ left: x(u.from) - l, width: ax.xr(u.to) - x(u.from), ["--done" as string]: `${Math.round(f * 100)}%` }} />
            </Fragment>
          );
        })}
        {inside ? <span className="lbl in">{b.groupName}</span> : null}
      </button>
      {dates ? <OvBarDates start={b.start >= from ? b.start : null} end={dateOk ? b.end : null} l={l} r={l + w} top={top} /> : null}
      {rightOk ? (
        <span className="sch-ovlbl" style={{ left: lblX, top: top + 4 }} aria-hidden="true">
          {b.groupName}
        </span>
      ) : null}
    </>
  );
}

/** 重なりの吹き出し（C3）：「10/14(水)・10/15(木) に 2現場」・業者名・現場ごとに 工程・n人・日付 と「工程表を開く」 */
function OverlapPop({ person, bars, anchor, onClose, onOpen }: { person: Person; bars: VBar[]; anchor: HTMLElement; onClose: () => void; onOpen: (b: VBar) => void }) {
  const days = [...new Set(bars.flatMap((b) => b.overlap))].sort();
  const groups = new Set(bars.map((b) => b.scheduleId)).size;
  const title = SCHEDULE_MSG.overlapPopTitle(daysLabel(days), groups);
  return (
    <Pop anchor={anchor} onClose={onClose} label={title} testId="sch-ov-overlap-pop" wide>
      <p className="sch-poph shu">
        <svg className="cri" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3l10 18H2zM12 10v4M12 17.5v.5" />
        </svg>
        {title}
      </p>
      <small className="muted">{person.name}</small>
      <ul className="sch-poplist">
        {bars.map((b) => (
          <li key={b.id}>
            <span className="t">
              <b>{b.groupName}</b>
              <small>{[b.name || "（名前なし）", b.start === b.end ? mdOf(b.start) : `${mdOf(b.start)}〜${mdOf(b.end)}`].join("・")}</small>
            </span>
            <button type="button" className="btn" onClick={() => onOpen(b)} data-testid="sch-ov-open">
              {SCHEDULE_MSG.openSchedule}
            </button>
          </li>
        ))}
      </ul>
      <p className="hint">{SCHEDULE_MSG.overlapNote}</p>
    </Pop>
  );
}

/** ほかのバーの吹き出し：現場名・工程名・日付・担当・進み・「工程表を開く」（人数は #297 でやめた） */
function BarPop({ m, info, anchor, onClose }: { m: Model; info: RowInfo; anchor: HTMLElement; onClose: () => void }) {
  const r = info.row;
  const who = assigneeName(m, r);
  const ovl = m.overlaps.byRow.get(r.id);
  return (
    <Pop anchor={anchor} onClose={onClose} label={`${info.item.groupName} ${r.name}`} testId="sch-ov-bar-pop">
      <b>{info.item.groupName}</b>
      <span>
        {r.name || "（名前なし）"}　{r.kind === "milestone" || r.start === (r.end ?? r.start) ? mdwOf(r.start) : `${mdwOf(r.start)}〜${mdwOf(r.end ?? r.start)}`}
      </span>
      <small className="muted">
        {[who || SCHEDULE_MSG.noAssignee, r.kind === "task" ? progressText(m.ov, r) : null].filter(Boolean).join("・")}
      </small>
      {ovl ? <span className="shu">{SCHEDULE_MSG.conflictBody(ovl.dates.map(mdOf).join("・"))}</span> : null}
      <Link className="btn" to={scheduleHref(info.item, r.id)} data-testid="sch-ov-open">
        {SCHEDULE_MSG.openSchedule}
      </Link>
    </Pop>
  );
}

/* ============================================================================
 * スマホ（P1）：日ごとの一覧
 * ========================================================================== */
function PhoneOverview() {
  const today = useMemo(() => todayLocal(), []);
  // 人の列（#297）：ON にした人だけ「延べ n人」と注記の人数の文
  const [crewOn] = useCrewColumn();
  const { toast, node: toastNode } = useToast();
  const reportsWait = usePendingReports().isPending;
  const [day, setDay] = useState(today);
  const [exporting, setExporting] = useState(false);
  const from = addDays(mondayOf(day), -7);
  const to = addDays(from, 34);
  const oq = useQuery({
    queryKey: ["schedule-overview", from, to],
    queryFn: () => api<ScheduleOverview>("GET", `/schedule/overview?from=${from}&to=${to}`),
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const m = useModel(oq.data);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const move = (n: number) => setDay((d) => addDays(d, n));

  const cards = useMemo(() => {
    if (!m) return [];
    // その日に作業する工程（稼働日）だけ。重なりはその日だけで数える
    const onDay = m.rows.filter((r) => r.row.kind === "task" && r.dates.includes(day));
    const ovl = overlapsOf(onDay.filter((r) => overlapCandidate(r.row)).map((r) => ({ siteId: r.item.scheduleId, rowId: r.row.id, key: assigneeKeyOf(r.row.assignee)!, dates: [day] })));
    const bySite = new Map<string, { item: ScheduleOverviewItem; rows: RowInfo[] }>();
    for (const r of onDay) {
      const c = bySite.get(r.item.scheduleId) ?? { item: r.item, rows: [] };
      c.rows.push(r);
      bySite.set(r.item.scheduleId, c);
    }
    const nameOf = new Map(m.ov.schedules.map((s) => [s.scheduleId, s.groupName]));
    return {
      list: [...bySite.values()].map((c) => ({ ...c, rows: c.rows.sort((a, b) => a.row.position - b.row.position) })),
      crew: onDay.reduce((n, r) => n + (r.row.crew ?? 0), 0),
      overlaps: ovl.count,
      others: (rowId: string) => [...new Set((ovl.byRow.get(rowId)?.others ?? []).map((o) => nameOf.get(o.siteId) ?? ""))].filter(Boolean),
    };
  }, [m, day]);
  // この先の工程（10/1）：翌日から7日のうちに始まる工程（読む期間は その週の前の週〜4週あとなので7日先まで入っている）
  const upcoming = useMemo(() => (m ? upcomingOf(m.rows, day, UPCOMING_DAYS) : []), [m, day]);

  if (oq.isPending) return <p className="hint">読み込み中…</p>;
  if (oq.isError && !oq.data) {
    return (
      <div className="sch-page ph ov">
        <div className="sch-ovtitle">
          <h1>{SCHEDULE_MSG.overviewTitle}</h1>
        </div>
        <OptionOff error={oq.error} />
      </div>
    );
  }
  if (!m || Array.isArray(cards)) return null;
  return (
    <div
      className="sch-page ph ov"
      data-testid="sch-overview-phone"
      onTouchStart={(e) => (touch.current = e.touches.length === 1 ? { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY } : null)}
      onTouchEnd={(e) => {
        const t = touch.current;
        touch.current = null;
        const c = e.changedTouches[0];
        if (!t || !c) return;
        const dx = c.clientX - t.x;
        if (Math.abs(dx) > 60 && Math.abs(c.clientY - t.y) < 40) move(dx < 0 ? 1 : -1);
      }}
    >
      <div className="sch-ovtitle">
        <h1>{SCHEDULE_MSG.overviewTitle}</h1>
        <span className="sp" />
        <button type="button" className="btn" onClick={() => setExporting(true)} data-testid="sch-ov-export">
          <CrIcon k="download" />
          {SCHEDULE_EXPORT_MSG.button}
        </button>
      </div>
      {exporting ? <ExportDialog target={{ kind: "overview", mode: "site", q: "", open: [], view: { from: mondayOf(day), to: addDays(mondayOf(day), 6) } }} onClose={() => setExporting(false)} toast={(t) => toast(t)} /> : null}
      <ReportsPanel toast={(t) => toast(t)} />
      {/* #179：報告の欄が届くまで、日の移動と表は見えない形で場所だけ取る */}
      <div style={reportsWait ? { visibility: "hidden" } : undefined} aria-busy={reportsWait || undefined} data-testid="sch-ov-phbody">
      <div className="sch-phnav ov">
        <button type="button" className="btn quiet sq" aria-label="前の日" onClick={() => move(-1)} data-testid="sch-ov-prev">
          <CrIcon k="back" />
        </button>
        <b className="sch-period" data-testid="sch-ov-day">
          {mdwLongOf(day)}
        </b>
        <button type="button" className="btn quiet sq" aria-label="次の日" onClick={() => move(1)} data-testid="sch-ov-next">
          <CrIcon k="right" />
        </button>
        <button type="button" className="btn" onClick={() => setDay(today)} data-testid="sch-today-btn">
          今日
        </button>
      </div>
      <div className="sch-ovchips">
        <span className="sch-chip" data-testid="sch-ov-sites">
          {SCHEDULE_MSG.sites(cards.list.length)}
        </span>
        {crewOn ? (
          <span className="sch-chip" data-testid="sch-ov-daycrew">
            {SCHEDULE_MSG.weekTotal(cards.crew)}人
          </span>
        ) : null}
        {cards.overlaps ? (
          <span className="sch-chip shu" data-testid="sch-ov-dayoverlap">
            {SCHEDULE_MSG.overlap} {cards.overlaps}
          </span>
        ) : null}
      </div>
      {oq.data?.truncated ? <p className="hint">{SCHEDULE_MSG.overviewTruncated}</p> : null}
      {cards.list.length === 0 ? <p className="hint sch-ovdayempty">{SCHEDULE_MSG.noWorkDay}</p> : null}
      {cards.list.map((c) => (
        <section key={c.item.scheduleId} className="paper sch-ovcard" data-testid="sch-ov-card">
          <Link className="hd" to={scheduleHref(c.item)}>
            <span className="t">
              <b>{c.item.groupName}</b>
              <small>{c.item.clientName}</small>
            </span>
            <CrIcon k="right" />
          </Link>
          {c.rows.map((r) => {
            const others = cards.others(r.row.id);
            return (
              <div key={r.row.id} className="it" data-testid="sch-ov-card-task">
                <div className="l">
                  <b>{r.row.name || "（名前なし）"}</b>
                  <small>{assigneeName(m, r.row) || SCHEDULE_MSG.noAssignee}</small>
                  {others.length ? (
                    <span className="ovl" data-testid="sch-ov-card-overlap">
                      <span className="sch-chip shu sm">{SCHEDULE_MSG.overlap}</span>
                      {SCHEDULE_MSG.alsoAt(others.join("・"))}
                    </span>
                  ) : null}
                </div>
                <div className="r">
                  <small>{m ? progressText(m.ov, r.row) : `進捗 ${r.row.progress ?? 0}%`}</small>
                </div>
              </div>
            );
          })}
        </section>
      ))}
      {upcoming.length ? (
        <section className="paper sch-ovcard sch-ovnext" data-testid="sch-ov-upcoming">
          <div className="hd">
            <span className="t">
              <b>{SCHEDULE_MSG.upcomingTitle(UPCOMING_DAYS)}</b>
            </span>
          </div>
          {upcoming.map((r) => (
            <Link key={r.row.id} className="it" to={scheduleHref(r.item, r.row.id)} data-testid="sch-ov-upcoming-task">
              <div className="l">
                <b>{r.row.name || "（名前なし）"}</b>
                <small>
                  {r.item.groupName}・{assigneeName(m, r.row) || SCHEDULE_MSG.noAssignee}
                </small>
              </div>
              <div className="r">
                <b className="num">{mdwOf(r.row.start)}</b>
              </div>
            </Link>
          ))}
        </section>
      ) : null}
      </div>
      <p className="hint sch-ovfootnote">{crewOn ? SCHEDULE_MSG.phoneNote : SCHEDULE_MSG.phoneNoteNoCrew}</p>
      {oq.isError ? <ErrorText>{messageOf(oq.error)}</ErrorText> : null}
      {toastNode}
    </div>
  );
}
